import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  PatchTeamRequest,
  TeamMember,
  TeamMemberIdentities,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class TeamAddIdentityCommand extends M8tCommand {
  static paths = [["team", "add-identity"]];
  static usage = Command.Usage({
    description: "Add one or more channel identities to an existing team member.",
    examples: [
      [
        "Add a Slack identity to an existing member",
        "$0 team add-identity idabest --slack U01ILA",
      ],
      [
        "Add multiple at once",
        "$0 team add-identity idabest --slack U01ILA --teams ilan@example.com",
      ],
    ],
  });

  handle = Option.String();
  telegram = Option.String("--telegram");
  slack = Option.String("--slack");
  teams = Option.String("--teams");
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const identities: TeamMemberIdentities = {};
    if (this.telegram) identities.telegram = this.telegram;
    if (this.slack) identities.slack = this.slack;
    if (this.teams) identities.teams = this.teams;

    if (Object.keys(identities).length === 0) {
      throw new LocalCliError({
        code: "USAGE",
        message: "at least one of --telegram, --slack, --teams is required.",
        hint: `Example: m8t team add-identity ${this.handle} --slack U01ILA`,
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const body: PatchTeamRequest = { addIdentities: identities };

    const member = await apiCall<TeamMember>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      {
        method: "PATCH",
        path: `/api/team/${encodeURIComponent(this.handle)}`,
        body,
      },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(member) + "\n");
      return 0;
    }

    const added = (Object.entries(identities) as [string, string][])
      .map(([ch, id]) => `${ch}=${id}`)
      .join(", ");
    this.context.stdout.write(
      `${colors.success("✓")} added ${added} to ${colors.field(member.handle)}.\n`,
    );
    return 0;
  }
}
